import { useCallback, useMemo } from 'react';

import type { ApolloLink } from '@apollo/client';
import { Apollo } from 'src/gql/generated';
import { removeWorkflowActionsFromCache } from 'src/gql/workflow-action-cache';
import { useGetWorkflowWithSteps } from 'src/hooks';
import { getSigningStepsFromWorkflow } from 'src/workflow';

export type ActionType = 'field' | 'cc';

/** Returns function that can create an action. */
export function useCreateActionType() {
  const [createWorkflowRecipient, { loading: creatingRecipient }] =
    Apollo.useCreateWorkflowRecipientMutation();

  return {
    createAction: useCallback(
      async ({
        actionPlacement,
        actionType,
        assignedPermissionId,
        isLauncher,
        recipientName,
        targetStepId,
        workflowId,
      }: {
        actionPlacement: Apollo.ActionPlacement;
        actionType: ActionType;
        /** The existing collaborator to assign. Ignored for the launcher. */
        assignedPermissionId: UUID;
        targetStepId: UUID | null;
        workflowId: UUID;
        isLauncher?: boolean;
        recipientName?: string | null;
      }) => {
        switch (actionType) {
          case 'cc':
          case 'field': {
            await createWorkflowRecipient({
              variables: {
                input: {
                  actionPlacement,
                  props: {
                    assignedPermissionId: isLauncher ? null : assignedPermissionId,
                    isLauncher: isLauncher ?? false,
                    type:
                      actionType === 'field'
                        ? Apollo.WorkflowRecipientType.Signer
                        : Apollo.WorkflowRecipientType.CarbonCopy,
                    workflowId,
                  },
                  recipientName: recipientName ?? null,
                  targetStepId,
                },
              },
            });
            break;
          }
        }
      },
      [createWorkflowRecipient]
    ),
    loading: creatingRecipient,
  };
}

/** An existing collaborator, by permission id, since role names repeat. */
export interface Assignee {
  isLauncher: boolean;
  permissionId: UUID;
}

const allActionTypes: ActionType[] = ['field', 'cc'];

/**
 * Returns the action types that are allowed for the given `assignee`
 * in a given `section` of a `workflow`.
 */
export function useAllowedActionTypes({
  assignee,
  workflowId,
}: {
  assignee: Assignee;
  workflowId: UUID;
}) {
  const { workflow } = useGetWorkflowWithSteps(workflowId);
  const orderedSteps = getSigningStepsFromWorkflow(workflow);
  const allActions = orderedSteps.flatMap(s => s.actions);
  // Ensures that the same recipient is not added multiple times for the same action (e.g., same recipient signs multiple times).
  const { availableActionsForRecipient } = useMemo(() => {
    const excludedActionTypes = new Set(
      allActions
        .filter(a => {
          // The launcher has no user or role, so it matches by its flag alone.
          if (assignee.isLauncher || a.permission.isLauncher)
            return assignee.isLauncher && a.permission.isLauncher;

          return a.permission.id === assignee.permissionId;
        })
        .map((assignedAction): ActionType => {
          return assignedAction.type === Apollo.WorkflowRecipientType.Signer ? 'field' : 'cc';
        })
    );
    const availableActionsForRecipient = allActionTypes
      .filter(actionType => {
        const isDuplicate = excludedActionTypes.has(actionType);
        return !isDuplicate;
      })
      .map(actionType => actionType);

    return { availableActionsForRecipient, excludedActionTypes };
  }, [allActions, assignee]);

  return { availableActionsForRecipient };
}

export function useDeleteActions() {
  const [deleteActions, { loading }] = Apollo.useDeleteWorkflowActionsMutation();
  const safeDeleteActions = useCallback(
    (
      options: Parameters<Apollo.DeleteWorkflowActionsMutationHookResult[0]>[0]
    ): Promise<ApolloLink.Result<Apollo.DeleteWorkflowActionsMutation>> => {
      const actionIds = options?.variables?.input.actionIds ?? [];
      return deleteActions({
        ...options,
        update: (cache, { data }) => {
          if (data)
            removeWorkflowActionsFromCache(
              cache,
              actionIds.map(id => ({ __typename: 'WorkflowRecipient', id }))
            );
        },
      });
    },
    [deleteActions]
  );

  return { deleteActions: safeDeleteActions, loading };
}
